import React, {useCallback, useMemo, useState} from "react"
import SaveIcon from "@mui/icons-material/Save";
import {IconButton} from "../../../features/management-v2/import/inputs";
import {useParams, useNavigate, useSearchParams} from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import {Layout} from "../Layout";
import {useEditContactGroup} from "../hooks/useEditContactGroup";
import {useGetContactGroup} from "../hooks/useGetContactGroup";
import {Contact, ContactGroup} from "../typedefs";
import {ErrorMessage} from "../ErrorMessage";
import {useGolemErrors} from "../../../lib/golem/useGolemErrors";
import {GolemText} from "../../../lib/golem/GolemField";
import {isNilObjectID} from "../../../lib/objectid/objectid";
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector";
import {useGetContactsList} from "../hooks/useGetContacts";
import {useGetContactGroupContacts} from "../hooks/useGetContactGroupContacts";
import PersonIcon from "@mui/icons-material/Person";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import {FieldError} from "@/lib/golem/FieldError";

export function ContactGroupsEdit() {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const redirect = searchParams.get("redirect");
    const {data, isLoading, error} = useGetContactGroup(id);

    const content = useMemo(() => {
        if (data) return <ContactGroupEditForm contactGroup={data} redirect={redirect}/>
        if (error) return <ErrorMessage text="error loading contact group"/>
        if (isLoading) return <div className="flex justify-center p-8"><LoadingSpinner/></div>
    }, [data, isLoading, error])

    return (
        <Layout menuCurrent="groups">
            <div className="bg-white shadow-md p-4 flex flex-col gap-4">
                {content}
            </div>
        </Layout>
    )
}

function ContactGroupEditForm({contactGroup, redirect}: {contactGroup: ContactGroup; redirect: string | null}) {
    const {name, setName, contactIDs, setContactIDs, isLoading, submit} = useEditContactGroup(contactGroup);
    const golemErrors = useGolemErrors();
    const navigate = useNavigate();
    const isDepartmentLinked = !isNilObjectID(contactGroup.departmentID);
    const getContacts = useGetContactsList(false);
    const getGroupContacts = useGetContactGroupContacts(contactGroup.id);
    const [showSelected, setShowSelected] = useState(isDepartmentLinked);

    const getColumns = useCallback((contact: Contact) => {
        const isUserLinked = !isNilObjectID(contact.userID);
        return [
            isUserLinked ? <PersonIcon fontSize="small" titleAccess="User-linked"/> : <AlternateEmailIcon fontSize="small" titleAccess="Manual"/>,
            `${contact.firstName} ${contact.lastName}`,
            contact.phoneMobileWork?.e164 ?? "",
            contact.phoneMobilePersonal?.e164 ?? "",
            contact.emailWork,
        ];
    }, []);

    return (
        <>
            <div className="flex items-center gap-2">
                {redirect && (
                    <button onClick={() => navigate(redirect)} className="text-grey hover:text-dark-grey">
                        <ArrowBackIcon fontSize="small"/>
                    </button>
                )}
                <h1 className="text-lg font-semibold">Edit Contact Group</h1>
            </div>
            {isDepartmentLinked && (
                <p className="text-sm text-grey">
                    This group is linked to a department and cannot be edited here.{" "}
                    <button
                        className="text-blue underline"
                        onClick={() => navigate(`/management/departments/edit/${contactGroup.departmentID}`)}
                    >
                        Edit Department
                    </button>
                </p>
            )}
            <GolemText label="Name" value={name} onChange={setName} errors={golemErrors} field="name" required disabled={isDepartmentLinked}/>
            <div>
                <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold">Contacts</p>
                    {!isDepartmentLinked && (
                        <button
                            onClick={() => setShowSelected(s => !s)}
                            className={`text-sm px-2 py-1 rounded border transition-colors ${showSelected ? "bg-blue text-white border-blue" : "border-light-grey text-grey hover:border-blue hover:text-blue"}`}
                        >
                            {showSelected ? "Show all contacts" : `Show members (${contactIDs.length})`}
                        </button>
                    )}
                </div>
                {showSelected ? (
                    <PaginatedSelector
                        headers={["Type", "Name", "Work Mobile", "Personal Mobile", "Email"]}
                        columnTemplate="80px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)"
                        getItems={getGroupContacts}
                        getColumns={getColumns}
                        nonClickableColumns={[0]}
                        selectedIDs={contactIDs}
                        setSelectedIDs={isDepartmentLinked ? undefined : setContactIDs}
                        disableSelect={isDepartmentLinked}
                        showSelectionCount={true}
                    />
                ) : (
                    <PaginatedSelector
                        headers={["Type", "Name", "Work Mobile", "Personal Mobile", "Email"]}
                        columnTemplate="80px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)"
                        getItems={getContacts}
                        getColumns={getColumns}
                        nonClickableColumns={[0]}
                        selectedIDs={contactIDs}
                        setSelectedIDs={isDepartmentLinked ? undefined : setContactIDs}
                        disableSelect={isDepartmentLinked}
                        showSelectionCount={true}
                    />
                )}
            </div>
            <FieldError error={golemErrors.fieldError("contactIDs")}/>
            {!isDepartmentLinked && (
                <IconButton text="Save" loading={isLoading} onClick={async () => {
                    try { await submit(); } catch (e) { golemErrors.setFromAxiosError(e); }
                }} icon={<SaveIcon/>} color="green"/>
            )}
        </>
    )
}
